import React from 'react'
import PropTypes from 'prop-types'
import { Box, Badge, Container } from '@chakra-ui/react'
import { loadImage } from 'integrations/unsplash'
import truncate from 'lodash/truncate'
import HTMLWrapper from 'lib/html-wrapper'
import { useRouter } from 'next/router'
import { useTranslation } from 'i18n'
import hasLoginRegisterLink from 'lib/has-login-register-link'

const FeaturedLayoutTwo = ({ data, meta, theme, dataTestId, isPFEAuthenticationEnabled, brandColor }) => {
  const router = useRouter()
  const { t } = useTranslation('common')
  const titleNode = () => {
    if (!meta.title) return false

    return (
      <Box
        as='h2'
        color={theme.sectionTitleColor}
        fontSize='sectionTitle'
        fontWeight='heading'
        fontFamily='heading'
        mb={4}
      >
        {meta.title}
      </Box>
    )
  }

  const statusNode = () => {
    if (!meta.showCardStatus) return false

    return (
      <Badge
        fontFamily='body'
        fontSize='body'
        rounded='site'
        variant='solid'
        className='ehq-text-gray-8 ehq-bg-gray-4 ehq-px-1 ehq-py-1 ehq-leading-4 ehq-font-normal'
      >
        {!!data.status && t(`common:common.${data.status.toLowerCase()}`)}
      </Badge>
    )
  }

  const CTAButtonNode = () => {
    if (!meta.showCtabutton || !data.ctaText) return false

    return (
      <Box
        alignItems='center'
        as='a'
        bg={theme.ctaBackgroundColor || 'brand'}
        boxShadow='site'
        color={theme.ctaTextColor || 'white'}
        _hover={{
          bg: theme.ctaBackgroundHoverColor || theme.ctaBackgroundColor || 'brand',
          color: theme.ctaTextHoverColor || theme.ctaTextColor || '#fff'
        }}
        d='inline-flex'
        fontFamily='body'
        fontSize='body'
        fontWeight='bold'
        href={(hasLoginRegisterLink(data.ctaLink) && isPFEAuthenticationEnabled && router.asPath !== '/') ? `${data.ctaLink}?next=${router.asPath}` : data.ctaLink}
        justifyContent='center'
        mt={2}
        px={2}
        py={1}
        rounded='site'
        textDecoration='none'
        className='featured-button'
      >
        {data.ctaText}
      </Box>
    )
  }

  return (
    <Box
      bg={theme.backgroundColor}
      px={[2, 4, 8, 16]}
      data-testid={dataTestId}
    >
      <Box bg={theme.backgroundColor} py={8} px={[4, 4, 0, 0]}>
        <Container maxW='container.lg' mx='auto' px={0}>
          {titleNode()}
          <Box
            boxShadow='site'
            bg={theme.backgroundColor}
            backgroundImage={`url(${loadImage(data.image, meta.imageSize || 800)})`}
            backgroundPosition={`${data.imgFocalX}% ${data.imgFocalY}%`}
            backgroundRepeat='no-repeat'
            backgroundSize='cover'
            d='flex'
            justifyContent='flex-end'
            p={[4, 4, 8, 8]}
            rounded='site'
            role='img'
            aria-label=''
          >
            <Box
              as={meta.showCtabutton ? 'div' : 'a'}
              bg={theme.cardBackgroundColor}
              boxShadow='site'
              href={data.ctaLink}
              overflow='hidden'
              rounded='site'
              w={['100%', '50%', '50%', '50%']}
            >
              <Box p={6}>
                {statusNode()}
                <Box
                  color={theme.textColor}
                  fontFamily='heading'
                  fontWeight='heading'
                  fontSize='heading'
                  wordBreak='break-word'
                  mb={0}
                  mt={1}
                >
                  {truncate(data.title, { length: 70 })}
                </Box>
                <Box
                  color={theme.textColor}
                  fontFamily='body'
                  fontWeight='body'
                  fontSize='body'
                  mt={2}
                  wordBreak='break-word'
                  className='featured-desc'
                >
                  <HTMLWrapper html={truncate(data.description, { length: 255 })} />
                </Box>
                {CTAButtonNode()}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      <style jsx global>{`
        :global(.featured-desc p) {
          margin-block-start: 1em;
          margin-block-end: 1em;
        }
        :global(.featured-desc a) {
          color: ${brandColor} !important;
        }
        :global(.featured-desc ul) {
          padding-inline-start: 40px !important;
        }
      `}</style>
    </Box>
  )
}

FeaturedLayoutTwo.propTypes = {
  data: PropTypes.object,
  meta: PropTypes.object,
  theme: PropTypes.object,
  dataTestId: PropTypes.string.isRequired,
  brandColor: PropTypes.string.isRequired
}

export default FeaturedLayoutTwo
